import { computed, DestroyRef, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Skus } from '@helper/skus';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { ProductRepositoryService } from '@service/product-repository.service';
import { BaseViewModel } from '../base-view-model';
import { CartModel } from '../cart/cart.model';
import { Product, Product_SkuColor } from '@proto/store/api/store/product';
import { Cart_Item } from '@proto/store/api/store/cart';
import { StockRemindDialogComponent } from '../favorite/stock-remind-dialog/stock-remind-dialog.component';
import { Toast } from '@helper/toast';
import { SizeInfo } from '@helper/size-info';
import { EventTracker } from '../../analytics/event-tracker.service';
import { RouterHistoryService } from '@service/router-history.service';
import { PageView } from '../../analytics/analytics-data';

import { isEqual } from 'lodash-es';

@Injectable()
export abstract class BaseProductModel extends BaseViewModel<Product> {
  abstract readonly colorId: Signal<string>;

  get currColor(): Signal<Product_SkuColor | undefined> {
    return this._selectColor.asReadonly();
  }

  get currSize(): Signal<SizeInfo | undefined> {
    return this._selectSize.asReadonly();
  }

  get count(): Signal<number> {
    return this._count.asReadonly();
  }

  get isPreview(): boolean {
    return this.router.url.includes(RoutePaths.PREVIEW);
  }

  readonly routerHistoryService = inject(RouterHistoryService);
  readonly eventTracker = inject(EventTracker);
  readonly currProductImg = computed(() => Skus.getImage500(this.currColor(), this.currSize()?.size));
  readonly availableSizes = computed(
    () => {
      const product = this.apiResult();
      const colorId = this.colorId();
      return product.sizes
        .map(
          size =>
            new SizeInfo(
              size,
              !colorId ? undefined : product.inventory[Skus.skuId(product.meta?.productId ?? '', colorId, size.id)]
            )
        )
        .filter(s => !!s.inventory?.num || !!s.inventory?.stockRemind);
    },
    {
      equal: (a: SizeInfo[], b: SizeInfo[]) => isEqual(a, b),
    }
  );
  readonly saleEventUrl = computed(() => {
    const product = this.apiResult();
    if (!product.promotion?.id || !product.promotion?.enabled || !product?.meta?.big?.nameEn) {
      return undefined;
    }
    return `/${RouteHelper.salesEvent(product.promotion.id, product?.meta?.big.nameEn)}`;
  });
  readonly lastSize: WritableSignal<string | undefined> = signal(undefined);
  readonly lastColorSize: WritableSignal<string | undefined> = signal(undefined);

  protected _selectColor: WritableSignal<Product_SkuColor | undefined> = signal(undefined, {
    equal: (a: Product_SkuColor | undefined, b: Product_SkuColor | undefined) => isEqual(a, b),
  });
  protected _selectSize: WritableSignal<SizeInfo | undefined> = signal(undefined, {
    equal: (a: SizeInfo | undefined, b: SizeInfo | undefined) => isEqual(a, b),
  });
  protected _count = signal(1);

  constructor(
    private dialog: MatDialog,
    private lativRepository: ProductRepositoryService,
    private cartModel: CartModel
  ) {
    super(Product.fromPartial({}));
  }

  abstract productUpdated(sku: Skus): void;

  getProduct(ref: DestroyRef, skuId: string): void {
    if (!skuId) {
      return;
    }
    const sku = Skus.fromId(skuId);
    this.lativRepository
      .getProduct({
        productId: sku.productId,
        preview: this.isPreview,
      })
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: product => {
          if (!sku.sizeId) {
            // eventTracker 需要完整的商品編號.
            skuId = Skus.skuId(sku.productId, product.colors[0]?.id, product.sizes[0]?.id);
          }
          this.eventTracker.onProduct(
            {
              id: skuId,
              name: product.meta?.name ?? '',
              category: product.meta?.big?.name ?? '',
              quantity: 1,
              price: product.meta?.promotionPrice || product.meta?.originPrice,
              promotionId: product.promotion?.id ?? '',
              promotionName: product.promotion?.name ?? '',
            },
            product.meta?.productId ?? '',
            this.eventTracker.getPageView() || PageView.HOME
          );
          this.setApiResult(product);
          this.productUpdated(sku);
        },
        error: err => this.setApiError(err),
      });
  }

  setColor(color: Product_SkuColor | undefined): void {
    this._selectColor.set(color);
  }

  setSize(size: SizeInfo): void {
    this._selectSize.set(size);
  }

  setCount(value: number): void {
    this._count.set(value);
  }

  addToCart(toast: MatSnackBar): void {
    if (!this.apiResult() || this.isPreview) {
      return;
    }
    if (!this._selectColor()) {
      Toast.open(toast, $localize`請選擇顏色`);
      return;
    }
    if (!this._selectSize()) {
      Toast.open(toast, $localize`請選擇尺碼`);
      return;
    }
    if (this.currSize()?.stockRemind) {
      StockRemindDialogComponent.open(this.dialog, this.apiResult().meta, this.currColor(), this.currSize());
      return;
    }
    if (!this._count()) {
      return;
    }
    this.cartModel.addCart(
      Cart_Item.fromPartial({
        skuId: Skus.skuId(
          this.apiResult().meta?.productId ?? '',
          this._selectColor()?.id ?? '',
          this._selectSize()?.size?.id ?? ''
        ),
        quantity: this._count(),
        selected: true,
        meta: this.apiResult().meta,
        published: this.apiResult().published,
        image: Skus.getImage500(this.currColor(), this.currSize()?.size),
        colorName: this._selectColor()?.color?.name ?? '',
        sizeName: this._selectSize()?.size?.name ?? '',
        sizeSuffix: this._selectSize()?.size?.suffix ?? '',
        promotionId: this.apiResult().promotion?.id,
        inventory: this._selectSize()?.inventory?.num ?? 0,
      }),
      toast
    );
  }
}
